import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { EventType } from '@kfolks/types'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { type AuditTimelineEntryDTO, requestCenterApi } from '@/services/request-center-api'

/**
 * `<AuditTimeline>` — Spec 3 replacement for `<AuditHistoryCard>`.
 *
 * Renders the unified audit log for a request: lifecycle, approval, file,
 * comment, ticket, and modification events — all on one timeline ordered
 * oldest → newest. The backend (`GET /requests/:id/audit`) already filters
 * comment events through the comments-visibility predicate, so the rows
 * we render here are exactly what the viewer is authorised to see.
 *
 * Per-row rendering is dispatched through a per-`EventType` lookup table
 * (`EVENT_RENDERERS`) so adding a new event type is one entry rather
 * than a switch-case in this component. Unknown event types fall back to
 * a generic `"<actor> performed <type>"` row so the timeline never
 * silently drops a row a future emitter adds.
 *
 * Five event types carry a "diff" payload (REQUEST_MODIFIED,
 * COMMENT_EDITED, COMMENT_DELETED, TICKET_STATUS_CHANGED,
 * TICKET_OTHER_CHANGE) — those rows expose an inline expand to show the
 * before / after blocks. Other rows render as compact one-liners.
 */

export interface AuditTimelineProps {
	requestId: string
}

export function AuditTimeline({ requestId }: AuditTimelineProps) {
	const [open, setOpen] = useState(false)
	const { data, isLoading, error } = useQuery({
		queryKey: ['request-audit', requestId],
		queryFn: () => requestCenterApi.listAudit(requestId),
	})

	const entries: AuditTimelineEntryDTO[] = data?.data ?? []
	const contentId = `audit-timeline-${requestId}`
	// Suppress 404s silently — the backend hides the request from
	// unrelated viewers the same way; same UX as the old card.
	const status = isAxiosError(error) ? error.response?.status : undefined

	return (
		<Card>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				aria-expanded={open}
				aria-controls={contentId}
				className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
			>
				<div className="flex items-center gap-2">
					<CardTitle className="text-base">Audit timeline</CardTitle>
					{entries.length > 0 ? (
						<span className="text-xs text-muted-foreground">
							· {entries.length} event{entries.length === 1 ? '' : 's'}
						</span>
					) : null}
				</div>
				<span aria-hidden className="text-lg leading-none text-muted-foreground">
					{open ? '▴' : '▾'}
				</span>
			</button>
			{open ? (
				<CardContent id={contentId}>
					{isLoading ? (
						<p className="text-sm text-muted-foreground">Loading…</p>
					) : error && status !== 404 ? (
						<p className="text-sm text-muted-foreground">No audit timeline available.</p>
					) : entries.length === 0 ? (
						<p className="text-sm text-muted-foreground">No events recorded yet.</p>
					) : (
						<ol className="space-y-3" data-testid="request-audit-timeline">
							{entries.map((entry) => (
								<TimelineRow key={entry.eventId} entry={entry} />
							))}
						</ol>
					)}
				</CardContent>
			) : null}
		</Card>
	)
}

// ─── Row + diff ───────────────────────────────────────────────────────────────

const DIFF_EVENT_TYPES = new Set<EventType>([
	EventType.REQUEST_MODIFIED,
	EventType.COMMENT_EDITED,
	EventType.COMMENT_DELETED,
	EventType.TICKET_STATUS_CHANGED,
	EventType.TICKET_OTHER_CHANGE,
])

function TimelineRow({ entry }: { entry: AuditTimelineEntryDTO }) {
	const [expanded, setExpanded] = useState(false)
	const renderer = EVENT_RENDERERS[entry.type] ?? FALLBACK_RENDERER
	const when = new Date(entry.timestamp)
	const whenStr = Number.isNaN(when.getTime()) ? entry.timestamp : when.toLocaleString()
	const hasDiff = DIFF_EVENT_TYPES.has(entry.type)

	return (
		<li className="border-l-2 border-muted pl-3" data-event-type={entry.type}>
			<div className="flex flex-wrap items-center gap-2 text-sm">
				<span aria-hidden className="text-muted-foreground">
					{renderer.icon}
				</span>
				<span className="font-medium">{renderer.label}</span>
				{renderer.renderInline?.(entry)}
				<span className="text-xs text-muted-foreground">{whenStr}</span>
			</div>
			<p className="text-xs text-muted-foreground">
				by <ActorName entry={entry} />
			</p>
			{hasDiff ? (
				<button
					type="button"
					onClick={() => setExpanded((prev) => !prev)}
					aria-expanded={expanded}
					className="mt-1 text-xs text-muted-foreground underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					{expanded ? 'Hide changes' : 'Show changes'}
				</button>
			) : null}
			{hasDiff && expanded ? <DiffBlock entry={entry} /> : null}
		</li>
	)
}

/**
 * Actor name with admin-bypass dual-identity: when the row carries
 * `originalApproverDisplayName`, render `"<admin> on behalf of <original>"`.
 * Otherwise the actor's display name (or userId fallback) on its own.
 */
function ActorName({ entry }: { entry: AuditTimelineEntryDTO }) {
	const actor = entry.actorDisplayName ?? shortenUuid(entry.actorUserId) ?? 'system'
	if (entry.originalApproverDisplayName) {
		return (
			<>
				<span className="font-medium">{actor}</span>
				<span className="text-muted-foreground"> on behalf of </span>
				<span className="font-medium">{entry.originalApproverDisplayName}</span>
			</>
		)
	}
	return <span className="font-medium">{actor}</span>
}

/**
 * Pretty-print the before/after blocks for a diff-bearing event. Each
 * event type stores its diff in a slightly different shape:
 *   - REQUEST_MODIFIED → `data.change = { field, before, after, source }`
 *   - COMMENT_EDITED   → `data.before = {...}, data.after = {...}`
 *   - COMMENT_DELETED  → `data.before = {...}` (no after — the row is gone)
 *   - TICKET_STATUS_CHANGED → `data.before, data.after` (raw status strings)
 *   - TICKET_OTHER_CHANGE   → `data.change = { field, before, after }`
 */
function DiffBlock({ entry }: { entry: AuditTimelineEntryDTO }) {
	const data = entry.data
	if (entry.type === EventType.REQUEST_MODIFIED || entry.type === EventType.TICKET_OTHER_CHANGE) {
		const change = data.change as { field?: string; before?: unknown; after?: unknown } | undefined
		if (!change) return null
		return (
			<dl className="mt-2 rounded border border-muted bg-muted/30 p-2 text-xs">
				<div className="flex gap-2">
					<dt className="font-medium text-muted-foreground">Field:</dt>
					<dd>{String(change.field ?? '—')}</dd>
				</div>
				<div className="flex gap-2">
					<dt className="font-medium text-muted-foreground">Before:</dt>
					<dd className="break-all">{stringify(change.before)}</dd>
				</div>
				<div className="flex gap-2">
					<dt className="font-medium text-muted-foreground">After:</dt>
					<dd className="break-all">{stringify(change.after)}</dd>
				</div>
			</dl>
		)
	}
	if (entry.type === EventType.TICKET_STATUS_CHANGED) {
		return (
			<dl className="mt-2 rounded border border-muted bg-muted/30 p-2 text-xs">
				<div className="flex gap-2">
					<dt className="font-medium text-muted-foreground">Before:</dt>
					<dd>{stringify(data.before)}</dd>
				</div>
				<div className="flex gap-2">
					<dt className="font-medium text-muted-foreground">After:</dt>
					<dd>{stringify(data.after)}</dd>
				</div>
			</dl>
		)
	}
	if (entry.type === EventType.COMMENT_EDITED || entry.type === EventType.COMMENT_DELETED) {
		const before = data.before as Record<string, unknown> | undefined
		const after = data.after as Record<string, unknown> | undefined
		return (
			<div className="mt-2 grid gap-2 rounded border border-muted bg-muted/30 p-2 text-xs sm:grid-cols-2">
				<div>
					<p className="font-medium text-muted-foreground">Before</p>
					<pre className="whitespace-pre-wrap break-all">{stringify(before)}</pre>
				</div>
				<div>
					<p className="font-medium text-muted-foreground">After</p>
					<pre className="whitespace-pre-wrap break-all">{after ? stringify(after) : '—'}</pre>
				</div>
			</div>
		)
	}
	return null
}

function stringify(v: unknown): string {
	if (v === undefined || v === null) return '—'
	if (typeof v === 'string') return v
	if (typeof v === 'number' || typeof v === 'boolean') return String(v)
	try {
		return JSON.stringify(v)
	} catch {
		return String(v)
	}
}

// ─── Display helpers ──────────────────────────────────────────────────────────

/**
 * Shorten a UUID into a readable handle when no display name resolved.
 * `196bf2ed-cf8f-4c5c-a679-50d8cc195183` → `196bf2ed…95183`. Anything that
 * isn't UUID-shaped (already a name, short id, etc.) passes through.
 */
function shortenUuid(value?: string): string | undefined {
	if (!value) return undefined
	const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
	if (!uuidPattern.test(value)) return value
	return `${value.slice(0, 8)}…${value.slice(-5)}`
}

// ─── Per-EventType renderers ──────────────────────────────────────────────────

interface EventRenderer {
	/** Single-character glyph rendered as a leading visual hint. Kept text-
	 *  only so we don't pull a new icon-set dependency just for the audit
	 *  card; substitute with `<MaterialIcon name="…" />` once we adopt one. */
	icon: string
	/** Headline label for the row (e.g. "Submitted", "Approved", "Comment created"). */
	label: string
	/** Optional inline renderer for the row's right-hand-side context — a
	 *  state-transition badge, a step name, an external link, etc. */
	renderInline?: (entry: AuditTimelineEntryDTO) => React.ReactNode
}

const EVENT_RENDERERS: Partial<Record<EventType, EventRenderer>> = {
	// ─── Request lifecycle ────────────────────────────────────────────────────
	[EventType.REQUEST_SUBMITTED]: { icon: '✉︎', label: 'Submitted' },
	[EventType.REQUEST_STATE_CHANGED]: {
		icon: '↻',
		label: 'State changed',
		renderInline: (entry) => {
			const data = entry.data as { fromState?: string | null; toState?: string }
			const transition = data.fromState ? `${data.fromState} → ${data.toState ?? '—'}` : (data.toState ?? '—')
			return <Badge variant="outline">{transition}</Badge>
		},
	},
	[EventType.REQUEST_APPROVED]: { icon: '✓', label: 'Approved (final step)' },
	[EventType.REQUEST_REJECTED]: {
		icon: '✗',
		label: 'Rejected',
		renderInline: (entry) => {
			const comment = (entry.data as { comment?: string }).comment
			return comment ? <span className="text-xs italic text-muted-foreground">“{comment}”</span> : null
		},
	},
	[EventType.REQUEST_CANCELLED]: {
		icon: '⊘',
		label: 'Cancelled',
		renderInline: (entry) => {
			const reason = (entry.data as { reason?: string }).reason
			return reason ? <span className="text-xs italic text-muted-foreground">“{reason}”</span> : null
		},
	},
	[EventType.REQUEST_COMPLETED]: { icon: '★', label: 'Completed' },
	[EventType.REQUEST_FAILED]: {
		icon: '⚠',
		label: 'Failed',
		renderInline: (entry) => {
			const err = (entry.data as { productiveError?: string }).productiveError
			return err ? <span className="text-xs italic text-muted-foreground">“{err}”</span> : null
		},
	},
	[EventType.REQUEST_MODIFIED]: {
		icon: '✎',
		label: 'Modified',
		renderInline: (entry) => {
			const change = (entry.data as { change?: { field?: string } }).change
			return change?.field ? <Badge variant="outline">{change.field}</Badge> : null
		},
	},
	[EventType.REQUEST_FILE_ATTACHED]: {
		icon: '📎',
		label: 'File attached',
		renderInline: (entry) => {
			const fieldId = (entry.data as { fieldId?: string }).fieldId
			return fieldId ? <Badge variant="outline">{fieldId}</Badge> : null
		},
	},
	[EventType.REQUEST_INFO_GATE_ANSWERED]: {
		icon: '?',
		label: 'Info gate answered',
		renderInline: (entry) => {
			const stepId = (entry.data as { stepId?: string }).stepId
			return stepId ? <Badge variant="outline">{stepId}</Badge> : null
		},
	},

	// ─── Per-step approval ────────────────────────────────────────────────────
	[EventType.REQUEST_APPROVAL_PENDING]: {
		icon: '◯',
		label: 'Approval pending',
		renderInline: (entry) => {
			const stepId = (entry.data as { stepId?: string; stepOrder?: number }).stepId
			return stepId ? <Badge variant="outline">{stepId}</Badge> : null
		},
	},
	[EventType.REQUEST_APPROVAL_APPROVED]: {
		icon: '✓',
		label: 'Step approved',
		renderInline: (entry) => {
			const data = entry.data as { stepId?: string; comment?: string }
			return (
				<>
					{data.stepId ? <Badge variant="outline">{data.stepId}</Badge> : null}
					{data.comment ? <span className="text-xs italic text-muted-foreground">“{data.comment}”</span> : null}
				</>
			)
		},
	},
	[EventType.REQUEST_APPROVAL_REJECTED]: {
		icon: '✗',
		label: 'Step rejected',
		renderInline: (entry) => {
			const data = entry.data as { stepId?: string; comment?: string }
			return (
				<>
					{data.stepId ? <Badge variant="outline">{data.stepId}</Badge> : null}
					{data.comment ? <span className="text-xs italic text-muted-foreground">“{data.comment}”</span> : null}
				</>
			)
		},
	},

	// ─── Ticket lifecycle ─────────────────────────────────────────────────────
	[EventType.TICKET_REQUESTED]: { icon: '🎫', label: 'Ticket requested' },
	[EventType.TICKET_CREATED]: {
		icon: '🎫',
		label: 'Ticket created',
		renderInline: (entry) => {
			const url = (entry.data as { externalTicketUrl?: string }).externalTicketUrl
			const externalId = (entry.data as { externalTicketId?: string }).externalTicketId
			if (!url) return externalId ? <Badge variant="outline">{externalId}</Badge> : null
			return (
				<a
					href={url}
					target="_blank"
					rel="noopener noreferrer"
					className="text-xs text-primary underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					{externalId ?? 'Open in Productive'} ↗
				</a>
			)
		},
	},
	[EventType.TICKET_REQUEST_FAILED]: {
		icon: '⚠',
		label: 'Ticket creation failed',
		renderInline: (entry) => {
			const err = (entry.data as { externalSystemError?: string }).externalSystemError
			return err ? <span className="text-xs italic text-muted-foreground">“{err}”</span> : null
		},
	},
	[EventType.TICKET_STATUS_CHANGED]: { icon: '↻', label: 'Ticket status changed' },
	[EventType.TICKET_CLOSED]: { icon: '✓', label: 'Ticket closed' },
	[EventType.TICKET_OTHER_CHANGE]: {
		icon: '✎',
		label: 'Ticket updated',
		renderInline: (entry) => {
			const change = (entry.data as { change?: { field?: string } }).change
			return change?.field ? <Badge variant="outline">{change.field}</Badge> : null
		},
	},

	// ─── Comment lifecycle ────────────────────────────────────────────────────
	[EventType.COMMENT_CREATED]: {
		icon: '💬',
		label: 'Comment',
		renderInline: (entry) => {
			const isInternal = (entry.data as { isInternal?: boolean }).isInternal
			return isInternal ? <Badge variant="secondary">internal</Badge> : null
		},
	},
	[EventType.COMMENT_EDITED]: { icon: '✎', label: 'Comment edited' },
	[EventType.COMMENT_DELETED]: { icon: '🗑', label: 'Comment deleted' },
	[EventType.COMMENT_MENTIONED]: {
		icon: '@',
		label: 'Mentioned',
		renderInline: (entry) => {
			const data = entry.data as { mentionedEmployeeId?: string; mentionedEmployeeDisplayName?: string }
			const display = data.mentionedEmployeeDisplayName ?? shortenUuid(data.mentionedEmployeeId)
			return display ? <Badge variant="outline">{display}</Badge> : null
		},
	},
}

const FALLBACK_RENDERER: EventRenderer = {
	icon: '•',
	label: 'Event',
	renderInline: (entry) => <Badge variant="outline">{entry.type}</Badge>,
}
